import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO, startOfDay } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';
import { BsCloudArrowUp, BsTrash } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  BookingRequestRead,
  BookingRequestReadStatusEnum,
  BookingRequestsService,
  FileBookingRequest,
  UserTypeEnum,
} from '../../../api';
import { bookingRequestsQueryKeys } from '../../../app/query-keys-factories/booking-requests';
import { useCurrentUser } from '../../hooks/auth';
import { handleApiError } from '../../utils/errorhandling';
import { FileUploader } from '../FileUploader';
import { FormFeedback } from '../FormFeedback';
import './ShiftDocumentArray.scss';

interface ShiftDocumentArrayProps {
  className?: string;
  isCanceled?: boolean;
  bookingRequest: BookingRequestRead;
}

const shiftDocumentArraySchema = z.object({
  files: z.array(
    z.object({
      file: z.string().url(),
      created_by: z.string(),
      file_name: z.string().optional(),
      id: z.string().optional(),
      created_at: z.string().optional(),
    })
  ),
});

export const ShiftDocumentArray = ({
  className,
  isCanceled,
  bookingRequest,
}: ShiftDocumentArrayProps) => {
  const { user } = useCurrentUser();
  const requestId = bookingRequest.id;
  const {
    formState: { errors },
    getValues,
    reset,
    control,
  } = useForm({
    resolver: zodResolver(shiftDocumentArraySchema),
  });
  const {
    fields: documentArrayFields,
    append: addDocument,
    remove: removeDocument,
  } = useFieldArray<FieldValues, string, 'id' | 'file'>({
    control,
    name: 'files',
  });

  const isProvider = useMemo(() => user?.user_type === UserTypeEnum.PROVIDER, [user]);

  // Calculate max booking date and determine which document buttons to show
  const { showAclLink, showTimecardLink, aclUrl, timecardUrl } = useMemo(() => {
    const isConfirmed = bookingRequest.status === BookingRequestReadStatusEnum.CONFIRMED;
    const bookings = bookingRequest.bookings;

    if (!isConfirmed || !bookings || bookings.length === 0) {
      return { showAclLink: false, showTimecardLink: false, aclUrl: '', timecardUrl: '' };
    }

    // Find the maximum booking date
    const maxBookingDate = bookings.reduce((maxDate, booking) => {
      const bookingDate = parseISO(booking.start_on);
      return bookingDate > maxDate ? bookingDate : maxDate;
    }, parseISO(bookings[0].start_on));

    const today = startOfDay(new Date());
    const maxDateAtStartOfDay = startOfDay(maxBookingDate);

    const showAcl = maxDateAtStartOfDay > today;
    const showTimecard = maxDateAtStartOfDay <= today;

    return {
      showAclLink: showAcl,
      showTimecardLink: showTimecard,
      aclUrl: `/staff-portal/assignments/confirmation-letter-legacy/${requestId}/`,
      timecardUrl: `/staff-portal/assignments/timecards-legacy/${requestId}/`,
    };
  }, [bookingRequest, requestId]);

  const { data: files, isLoading } = useQuery({
    queryKey: bookingRequestsQueryKeys.fileAttachments(requestId),
    queryFn: async () =>
      await BookingRequestsService.bookingRequestsAttachmentsRetrieve({ requestId }),
  });

  const queryClient = useQueryClient();
  const mutationBookingAttachmentsUpdate = useMutation({
    mutationFn: async (data: FileBookingRequest) => {
      await BookingRequestsService.bookingRequestsAttachmentsUpdate({
        requestId,
        requestBody: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(bookingRequestsQueryKeys.fileAttachments(requestId));
    },
  });

  const addDocumentHandler = useCallback(() => {
    const now = new Date();
    const created_at = format(
      new Date(now.valueOf() + now.getTimezoneOffset() * 60 * 1000),
      'yyyy-MM-dd'
    );
    addDocument({
      file: null,
      created_by: user?.id,
      file_name: '',
      created_at,
    });
  }, [addDocument, user?.id]);

  useEffect(() => {
    reset(files);
  }, [reset, files]);

  const onStatusChange = async () => {
    try {
      const data = getValues()
        .files.filter((item) => !!item.file)
        .map((item) => ({
          ...item,
          file_name: item.file_name || item.file?.split('/').pop(),
        }));

      if (!data.length && !files?.files.length) return;

      await mutationBookingAttachmentsUpdate.mutateAsync({ files: data });

      toast.success('Assignment Request updated successfully');
    } catch (error) {
      handleApiError(error);
    }
  };

  const onRemove = (index: number) => {
    removeDocument(index);
    onStatusChange();
  };

  const canAdd = useMemo(() => {
    return documentArrayFields.length === 0 || !documentArrayFields.some((item) => !item.file);
  }, [documentArrayFields]);

  return (
    <Form.Group className={className}>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <h5 className="text-section-name--dark-brand documents-header mb-0">Documents</h5>
        {!isProvider && (showAclLink || showTimecardLink) && (
          <div className="d-flex gap-2">
            {showAclLink && (
              <a href={aclUrl} target="_blank" rel="noopener noreferrer" className="!text-primary">
                View ACL
              </a>
            )}
            {showTimecardLink && (
              <a
                href={timecardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="!text-primary"
              >
                View Timecard
              </a>
            )}
          </div>
        )}
      </div>
      {!isLoading &&
        documentArrayFields.map((field, index) => (
          <div key={field.id}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <FileUploader
                disabledPreview={true}
                maxSize={30}
                accept=".pdf, .jpg, .jpeg, .png, .haif, .doc, .docx, .txt"
                name={`files.${index}.file`}
                containerClassName="w-100"
                control={control}
                dest="healthcare_documents"
                required={false}
                isClearable={false}
                isInvalid={!!errors?.files?.[index]}
                onChange={() => onStatusChange()}
                disabledDownload={mutationBookingAttachmentsUpdate.isLoading}
                disabled={mutationBookingAttachmentsUpdate.isLoading}
              />
              {!isProvider && !isCanceled && (
                <BsTrash
                  className={`ms-2 remove-btn ${
                    mutationBookingAttachmentsUpdate.isLoading ? 'disabled' : ''
                  }`}
                  style={{
                    visibility: user?.id === field['created_by'] ? 'visible' : 'hidden',
                  }}
                  onClick={() => onRemove(index)}
                />
              )}
            </div>
            <FormFeedback clientError={errors?.files?.[index]?.file} />
          </div>
        ))}
      <div className="d-flex">
        <Button
          className="d-flex align-items-center"
          type="button"
          variant="outline-primary"
          disabled={mutationBookingAttachmentsUpdate.isLoading || !canAdd}
          onClick={addDocumentHandler}
        >
          <BsCloudArrowUp size="1.5rem" className="me-1" /> Upload Document
        </Button>
      </div>
    </Form.Group>
  );
};
